import type {
  BulkScheduleRequestDTO,
  CheckAvailabilityRequestDTO,
  CreateBookingDTO,
  CreatePrivateLessonTemplateDTO,
  SellPrivateLessonGroupPackageDTO,
  SellPrivateLessonPrivateSharedPackageDTO,
  SellPrivateLessonPackageDTO,
  UpdateBookingDTO,
  UpdatePrivateLessonTemplateDTO,
} from "@/modules/private-lessons/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { privateLessonsApi } from "./client";

// ===== Private Lesson Templates =====
export const privateLessonTemplatesKeys = {
  all: ["private-lessons", "templates"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["private-lessons", "templates", "list", q] as const,
  detail: (id: string) =>
    ["private-lessons", "templates", "detail", id] as const,
};

export function usePrivateLessonTemplatesQuery(
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: privateLessonTemplatesKeys.list(q),
    queryFn: () => privateLessonsApi.templates.list(q),
  });
}

export function usePrivateLessonTemplateQuery(id: string) {
  return useQuery({
    queryKey: privateLessonTemplatesKeys.detail(id),
    queryFn: () => privateLessonsApi.templates.get(id),
    enabled: !!id,
  });
}

export function usePrivateLessonTemplateUsageStatsQuery(id: string) {
  return useQuery({
    queryKey: ["private-lessons", "templates", "usage-stats", id],
    queryFn: () => privateLessonsApi.templates.getUsageStats(id),
    enabled: !!id,
  });
}

export function useCreatePrivateLessonTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePrivateLessonTemplateDTO) =>
      privateLessonsApi.templates.create(input),
    onSuccess: () => {
      toast.success("ສ້າງ template ແພັກເກດຮຽນສ່ວນຕົວສຳເລັດ");
      qc.invalidateQueries({
        queryKey: privateLessonTemplatesKeys.all,
      });
    },
    onError: () => toast.error("ສ້າງ template ແພັກເກດຮຽນສ່ວນຕົວບໍ່ສຳເລັດ"),
  });
}

export function useUpdatePrivateLessonTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePrivateLessonTemplateDTO) =>
      privateLessonsApi.templates.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດ template ແພັກເກດຮຽນສ່ວນຕົວສຳເລັດ");
      qc.invalidateQueries({
        queryKey: privateLessonTemplatesKeys.detail(id),
      });
      qc.invalidateQueries({
        queryKey: privateLessonTemplatesKeys.all,
      });
    },
    onError: () => toast.error("ອັບເດດ template ແພັກເກດຮຽນສ່ວນຕົວບໍ່ສຳເລັດ"),
  });
}

export function useTogglePrivateLessonTemplateStatus(id: string) {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (status: "active" | "inactive") =>
      privateLessonsApi.templates.toggleStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: privateLessonTemplatesKeys.detail(id),
      });
      qc.invalidateQueries({
        queryKey: privateLessonTemplatesKeys.all,
      });
    },
  });

  const run = (status: "active" | "inactive") =>
    new Promise<void>((resolve, reject) => {
      base.mutate(status, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// ===== Private Lesson Packages =====
export const privateLessonPackagesKeys = {
  all: ["private-lessons", "packages"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["private-lessons", "packages", "list", q] as const,
  detail: (id: string) =>
    ["private-lessons", "packages", "detail", id] as const,
};

export function usePrivateLessonPackagesQuery(
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: privateLessonPackagesKeys.list(q),
    queryFn: () => privateLessonsApi.packages.list(q),
  });
}

export function usePrivateLessonPackageQuery(id: string) {
  return useQuery({
    queryKey: privateLessonPackagesKeys.detail(id),
    queryFn: () => privateLessonsApi.packages.getById(id),
    enabled: !!id,
  });
}

export function useCancelPrivateLessonPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; cancelReason: string }) =>
      privateLessonsApi.packages.cancel(input.id, {
        cancelReason: input.cancelReason,
      }),
    onSuccess: (_, variables) => {
      qc.invalidateQueries({
        queryKey: privateLessonPackagesKeys.all,
      });
      qc.invalidateQueries({
        queryKey: privateLessonPackagesKeys.detail(variables.id),
      });
      toast.success("ຍົກເລີກແພັກເກດສຳເລັດ");
    },
    onError: () => {
      toast.error("ຍົກເລີກແພັກເກດລົ້ມເຫຼວ");
    },
  });
}

// ===== Private Lesson Bookings =====
export const privateLessonBookingsKeys = {
  all: ["private-lessons", "bookings"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) =>
    ["private-lessons", "bookings", "list", q] as const,
  calendar: (
    start: string,
    end: string,
    filters?: {
      roomId?: string;
      instructorId?: string;
      status?: string;
    },
  ) =>
    ["private-lessons", "bookings", "calendar", start, end, filters] as const,
  detail: (id: string) =>
    ["private-lessons", "bookings", "detail", id] as const,
};

export function useBookingsCalendarQuery(
  start: string,
  end: string,
  filters?: {
    roomId?: string;
    instructorId?: string;
    status?: string;
  },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: privateLessonBookingsKeys.calendar(start, end, filters),
    queryFn: () => privateLessonsApi.bookings.getCalendar(start, end, filters),
    enabled: (options?.enabled ?? true) && !!start && !!end,
  });
}

export function usePrivateLessonBookingsQuery(
  query: Partial<OffsetPageQueryDTO> = {},
) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: privateLessonBookingsKeys.list(q),
    queryFn: () => privateLessonsApi.bookings.list(q),
  });
}

export function usePrivateLessonBookingQuery(id: string) {
  return useQuery({
    queryKey: privateLessonBookingsKeys.detail(id),
    queryFn: () => privateLessonsApi.bookings.getById(id),
    enabled: !!id,
  });
}

export function useCreatePrivateLessonBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBookingDTO) =>
      privateLessonsApi.bookings.create(input),
    onSuccess: () => {
      toast.success("ຈອງຮຽນສຳເລັດ");
      qc.invalidateQueries({
        queryKey: privateLessonBookingsKeys.all,
      });
      qc.invalidateQueries({
        queryKey: privateLessonPackagesKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ຈອງຮຽນບໍ່ສຳເລັດ");
    },
  });
}

export function useUpdatePrivateLessonBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateBookingDTO }) =>
      privateLessonsApi.bookings.update(id, input),
    onSuccess: (_, variables) => {
      toast.success("ອັບເດດການຈອງສຳເລັດ");
      qc.invalidateQueries({
        queryKey: privateLessonBookingsKeys.detail(variables.id),
      });
      qc.invalidateQueries({
        queryKey: privateLessonBookingsKeys.all,
      });
      qc.invalidateQueries({
        queryKey: privateLessonPackagesKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ອັບເດດການຈອງບໍ່ສຳເລັດ");
    },
  });
}

export function useCancelPrivateLessonBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => privateLessonsApi.bookings.cancel(id),
    onSuccess: (_, id) => {
      toast.success("ຍົກເລີກການຈອງສຳເລັດ");
      qc.invalidateQueries({
        queryKey: privateLessonBookingsKeys.detail(id),
      });
      qc.invalidateQueries({
        queryKey: privateLessonBookingsKeys.all,
      });
      qc.invalidateQueries({
        queryKey: privateLessonPackagesKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ຍົກເລີກການຈອງບໍ່ສຳເລັດ");
    },
  });
}

export function usePrivateLessonBookingInstructorAttendeesQuery(
  bookingId: string,
) {
  return useQuery({
    queryKey: [
      ...privateLessonBookingsKeys.detail(bookingId),
      "instructor-attendees",
    ],
    queryFn: () =>
      privateLessonsApi.bookings.getInstructorAttendees(bookingId),
    enabled: !!bookingId,
  });
}

export function useCorrectInstructorCheckInBooking(bookingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      wrongCheckInId: string;
      correctInstructorId: string;
      reason: string;
    }) =>
      privateLessonsApi.bookings.correctInstructorCheckIn(bookingId, body),
    onSuccess: () => {
      toast.success("ແກ້ໄຂການກວດຊື່ຄູສອນສຳເລັດ");
      qc.invalidateQueries({
        queryKey: privateLessonBookingsKeys.detail(bookingId),
      });
      qc.invalidateQueries({
        queryKey: privateLessonBookingsKeys.all,
      });
      qc.invalidateQueries({
        queryKey: [
          ...privateLessonBookingsKeys.detail(bookingId),
          "instructor-attendees",
        ],
      });
      qc.invalidateQueries({ queryKey: ["attendance"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ແກ້ໄຂການກວດຊື່ຄູສອນບໍ່ສຳເລັດ");
    },
  });
}

export function useCheckInPrivateLessonBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      instructorIds?: string[];
      memberStudentIds?: string[];
    }) => privateLessonsApi.bookings.checkIn(id, body),
    onSuccess: (_, { id }) => {
      toast.success("ກວດສອບເຂົ້າສຳເລັດ");
      qc.invalidateQueries({
        queryKey: privateLessonBookingsKeys.detail(id),
      });
      qc.invalidateQueries({
        queryKey: privateLessonBookingsKeys.all,
      });
      qc.invalidateQueries({
        queryKey: privateLessonPackagesKeys.all,
      });
      qc.invalidateQueries({
        queryKey: ["private-lessons", "bookings", "attendance", id],
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ກວດສອບເຂົ້າບໍ່ສຳເລັດ");
    },
  });
}

export function usePrivateLessonBookingAttendanceQuery(id: string) {
  return useQuery({
    queryKey: ["private-lessons", "bookings", "attendance", id],
    queryFn: () => privateLessonsApi.bookings.getAttendance(id),
    enabled: !!id,
  });
}

export function useCompletePrivateLessonBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => privateLessonsApi.bookings.complete(id),
    onSuccess: (_, id) => {
      toast.success("ອັບເດດສະຖານະຈອງເປັນສຳເລັດ");
      qc.invalidateQueries({
        queryKey: privateLessonBookingsKeys.detail(id),
      });
      qc.invalidateQueries({
        queryKey: privateLessonBookingsKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ອັບເດດສະຖານະບໍ່ສຳເລັດ");
    },
  });
}

export function useDeletePrivateLessonTemplate() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => privateLessonsApi.templates.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: privateLessonTemplatesKeys.all,
      });
    },
    onError: () => toast.error("ລຶບ template ແພັກເກດຮຽນສ່ວນຕົວບໍ່ສຳເລັດ"),
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// ===== Sell Private Lesson Package =====
export const sellPrivateLessonPackageKeys = {
  all: ["private-lessons", "sell"] as const,
  checkActivePackage: (studentId: string) =>
    ["private-lessons", "sell", "check-active-package", studentId] as const,
};

export function useCheckActivePrivateLessonPackageQuery(
  studentId: string | null,
) {
  return useQuery({
    queryKey: sellPrivateLessonPackageKeys.checkActivePackage(studentId ?? ""),
    queryFn: () => {
      if (!studentId) throw new Error("Student ID is required");
      return privateLessonsApi.sell.checkActivePackage(studentId);
    },
    enabled: !!studentId,
  });
}

export function useSellPrivateLessonPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SellPrivateLessonPackageDTO) =>
      privateLessonsApi.sell.sellPackage(input),
    onSuccess: () => {
      toast.success("ຂາຍແພັກເກດຮຽນສ່ວນຕົວສຳເລັດ");
      qc.invalidateQueries({
        queryKey: sellPrivateLessonPackageKeys.all,
      });
    },
    onError: () => toast.error("ຂາຍແພັກເກດຮຽນສ່ວນຕົວບໍ່ສຳເລັດ"),
  });
}

export function useSellPrivateLessonGroupPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SellPrivateLessonGroupPackageDTO) =>
      privateLessonsApi.sell.sellGroupPackage(input),
    onSuccess: () => {
      toast.success("ຂາຍແພັກເກດກຸ່ມສຳເລັດ");
      qc.invalidateQueries({
        queryKey: sellPrivateLessonPackageKeys.all,
      });
    },
    onError: () => toast.error("ຂາຍແພັກເກດກຸ່ມບໍ່ສຳເລັດ"),
  });
}

export function useSellPrivateLessonPrivateSharedPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SellPrivateLessonPrivateSharedPackageDTO) =>
      privateLessonsApi.sell.sellPrivateSharedPackage(input),
    onSuccess: () => {
      toast.success("ຂາຍ Private Package ສຳເລັດ");
      qc.invalidateQueries({
        queryKey: sellPrivateLessonPackageKeys.all,
      });
    },
    onError: () => toast.error("ຂາຍ Private Package ບໍ່ສຳເລັດ"),
  });
}

// ===== Bulk Schedule Private Lessons =====
export const bulkScheduleKeys = {
  all: ["private-lessons", "bulk-schedule"] as const,
};

export function useCheckBookingAvailability() {
  return useMutation({
    mutationFn: (input: CheckAvailabilityRequestDTO) =>
      privateLessonsApi.bookings.checkAvailability(input),
  });
}

export function useBulkSchedulePrivateLessons() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkScheduleRequestDTO) =>
      privateLessonsApi.bookings.bulkSchedule(input),
    onSuccess: (data) => {
      toast.success(`ຈອງຮຽນສຳເລັດ ${data.createdCount} ຄັ້ງ`);
      qc.invalidateQueries({
        queryKey: privateLessonPackagesKeys.all,
      });
      qc.invalidateQueries({
        queryKey: privateLessonBookingsKeys.all,
      });
      qc.invalidateQueries({
        queryKey: bulkScheduleKeys.all,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "ຈອງຮຽນບໍ່ສຳເລັດ");
    },
  });
}

// Bulk delete mutation
export function useBulkDeletePrivateLessonTemplates() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (ids: string[]) =>
      privateLessonsApi.templates.bulkDelete({ ids }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: privateLessonTemplatesKeys.all,
      });
    },
  });

  const run = (ids: string[]) =>
    new Promise<{ deletedCount: number }>((resolve, reject) => {
      base.mutate(ids, {
        onSuccess: (result) => resolve(result),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// Bulk toggle status mutation
export function useBulkTogglePrivateLessonTemplateStatus() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (input: { ids: string[]; status: "active" | "inactive" }) =>
      privateLessonsApi.templates.bulkToggleStatus(input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: privateLessonTemplatesKeys.all,
      });
    },
  });

  const run = (input: { ids: string[]; status: "active" | "inactive" }) =>
    new Promise<{ updatedCount: number }>((resolve, reject) => {
      base.mutate(input, {
        onSuccess: (result) => resolve(result),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}
